// src/modules/app-views/crm/pages/Boards/modals/CrmParametersModal/tabs/SourcesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm, Modal, Form, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SourcesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  const [sources, setSources] = useState(() => {
    const moduleId = storedModule?.id || 'crm';
    const raw = appService.getModulePreferences({ moduleId, category: 'sources' });
    return Array.isArray(raw) ? raw : [];
  });

  const visibleSources = useMemo(() => sources.filter(s => !s.removed), [sources]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (showModal) {
      if (editingRecord) {
        form.setFieldsValue({ label: editingRecord.label || '' });
      } else {
        form.resetFields();
      }
    }
  }, [showModal, editingRecord, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const moduleId = storedModule?.id || 'crm';
      const labelValue = (values.label || '').trim();

      // Check for duplicates
      const isDuplicate = sources.some(
        s =>
          s.uid !== editingRecord?.uid &&
          !s.removed &&
          (s.label || '').toLowerCase() === labelValue.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);

      let updated;
      if (editingRecord) {
        updated = sources.map(s =>
          s.uid === editingRecord.uid ? { ...s, label: labelValue } : s
        );
      } else {
        updated = [
          ...sources,
          { uid: generateUidV4(), label: labelValue, order: sources.length },
        ];
      }

      await appService.updateModulePreferences({ moduleId, category: 'sources', data: updated });

      setSources(updated);
      appService.message({ flag: 's', text: 'saved_successfully' });
      setShowModal(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // validation error
      console.error('[SourcesTab] Save error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
    } finally {
      setLoading(false);
    }
  }, [form, sources, editingRecord, setLoading, storedModule?.id]);

  const handleDelete = useCallback(
    async record => {
      const moduleId = storedModule?.id || 'crm';
      setLoading(true);
      try {
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'sources',
          uid: record.uid,
        });

        let updated;
        if (isInUse) {
          updated = sources.map(s =>
            s.uid === record.uid ? { ...s, removed: true } : s
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          updated = sources.filter(s => s.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        await appService.updateModulePreferences({ moduleId, category: 'sources', data: updated });
        setSources(updated);
      } catch (error) {
        console.error('[SourcesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [sources, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('label'),
      dataIndex: 'label',
      render: text => translateX(text) || text,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleSources}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_source')}
        </Button>
      </Flex>

      <Modal
        title={editingRecord ? translateX('edit') : translateX('add_source')}
        open={showModal}
        onCancel={handleCancel}
        destroyOnHidden
        centered
        mousePosition={mousePosition}
        getContainer={document.body}
        width={400}
        footer={
          <Flex justify="end" style={{ paddingTop: 8 }}>
            <Space>
              <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
                {translateX('cancel')}
              </Button>
              <Button type="primary" loading={loading} icon={<CheckOutlined />} onClick={handleSave}>
                {translateX('save')}
              </Button>
            </Space>
          </Flex>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="label"
            label={translateX('label')}
            rules={[{ required: true, message: () => translateX('required_field') }]}
          >
            <Input maxLength={50} style={{ height: 40 }} disabled={loading} />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SourcesTab;
